import { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { UploadCloud, CopyX, Images, HardDrive, Printer } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CHUNK = 3;

const STATUS_STYLE = {
  queued: ["QUEUED", "text-[#6E6553]"],
  uploading: ["ADDING", "text-[#C4562F] animate-pulse"],
  done: ["ADDED", "text-[#3C3527]"],
  duplicate: ["DUPLICATE", "text-[#C4562F]"],
  error: ["SKIPPED", "text-[#75332D]"],
};

const isVideoFile = (f) => f.type.startsWith("video/") || /\.(mov|mp4|m4v)$/i.test(f.name);

export default function UploadZone({ count, bytes, dupesCaught, onUploaded }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);

  const patchRows = (names, patch) =>
    setRows((rs) => rs.map((r) => (names.includes(r.name) ? { ...r, ...patch } : r)));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || busy) return;
    setBusy(true);
    setRows(files.map((f) => ({ name: f.name, status: "queued", note: "" })));
    let added = 0;
    let dupes = 0;
    let errs = 0;
    const images = files.filter((f) => !isVideoFile(f));
    const videos = files.filter(isVideoFile);
    const groups = [];
    for (let i = 0; i < images.length; i += CHUNK) groups.push(images.slice(i, i + CHUNK));
    videos.forEach((v) => groups.push([v]));
    for (const chunk of groups) {
      const names = chunk.map((f) => f.name);
      patchRows(names, { status: "uploading" });
      const fd = new FormData();
      chunk.forEach((f) => fd.append("files", f));
      let data = null;
      for (let attempt = 0; attempt < 2 && !data; attempt++) {
        try {
          ({ data } = await axios.post(`${API}/photos/upload`, fd));
        } catch {
          data = null;
        }
      }
      if (data) {
        patchRows(data.added.map((a) => a.name), { status: "done", note: "ready" });
        data.duplicates.forEach((d) =>
          patchRows([d.name], { status: "duplicate", note: `matches ${d.similar_to}` })
        );
        data.errors.forEach((e) => patchRows([e.name], { status: "error", note: e.reason }));
        added += data.added.length;
        dupes += data.duplicates.length;
        errs += data.errors.length;
        onUploaded();
      } else {
        patchRows(names, { status: "error", note: "re-drop to retry" });
        errs += chunk.length;
      }
    }
    setBusy(false);
    if (added) toast.success(`${added} photo${added > 1 ? "s" : ""} added to the gallery`);
    if (dupes) toast(`${dupes} duplicate${dupes > 1 ? "s" : ""} skipped`);
    if (errs) toast.error(`${errs} file${errs > 1 ? "s" : ""} couldn't be read`);
    onUploaded();
  };

  return (
    <section id="upload" data-testid="upload-section" className="px-6 sm:px-10 lg:px-16 py-24">
      <div className="text-center mb-14">
        <p className="text-[11px] tracking-[0.35em] uppercase text-[#75332D] mb-4">
          Everyone's welcome · print-worthy
        </p>
        <h2 className="font-serif font-normal text-4xl sm:text-5xl text-[#3C3527]">Add Your Photos</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="col-span-full md:col-span-8">
          <motion.div
            data-testid="upload-dropzone"
            onClick={() => !busy && inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              handleFiles(e.dataTransfer.files);
            }}
            whileHover={{ scale: busy ? 1 : 1.005 }}
            className={`cursor-pointer border border-dashed p-10 sm:p-14 transition-colors duration-300 ${
              dragging ? "border-[#75332D] bg-[#75332D]/5" : "border-[#3C3527]/25 bg-white/40 hover:border-[#3C3527]/60"
            }`}
          >
            <UploadCloud className="text-[#75332D] mb-6" size={28} strokeWidth={1.5} />
            <p className="font-serif font-normal text-2xl sm:text-3xl text-[#3C3527]">
              {busy ? "Developing your photos…" : "Drop your photos here"}
            </p>
            <p className="mt-3 text-sm font-light text-[#6E6553]">
              or click to browse — select them all at once, that's fine
            </p>
            <p className="mt-6 text-[10px] tracking-[0.25em] uppercase text-[#6E6553]">
              JPG · PNG · WEBP · HEIC · MOV · MP4
            </p>
            <input
              ref={inputRef}
              data-testid="upload-input"
              type="file"
              multiple
              accept="image/*,video/*,.heic,.heif,.mov"
              className="hidden"
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </motion.div>
          {rows.length > 0 && (
            <div
              data-testid="upload-log"
              data-lenis-prevent
              className="mt-6 border border-[#E0D8C6] bg-white/60 max-h-72 overflow-y-auto text-xs"
            >
              {rows.map((r, i) => {
                const [label, cls] = STATUS_STYLE[r.status];
                return (
                  <div
                    key={`${r.name}-${i}`}
                    data-testid={`upload-row-${i}`}
                    className="flex items-center justify-between gap-4 px-4 py-2 border-b border-[#E0D8C6]/60 last:border-0"
                  >
                    <span className="truncate text-[#3C3527]">{r.name}</span>
                    <span className="flex items-center gap-3 shrink-0">
                      {r.note && <span className="text-[10px] text-[#6E6553]">{r.note}</span>}
                      <span className={`text-[10px] tracking-[0.15em] uppercase ${cls}`}>{label}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <aside className="col-span-full md:col-span-4 flex flex-col gap-4">
          {[
            { icon: Images, label: "Photos in the gallery", value: count, testid: "stats-photo-count" },
            { icon: HardDrive, label: "Downloads", value: "Free", testid: "stats-archive-size" },
            { icon: CopyX, label: "The weekend", value: "Oct 2–4", testid: "stats-dupes" },
            { icon: Printer, label: "Print-ready", value: "300 DPI", testid: "stats-print" },
          ].map(({ icon: Icon, label, value, testid }) => (
            <div key={label} className="border border-[#E0D8C6] bg-white/40 p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon size={16} className="text-[#75332D]" strokeWidth={1.5} />
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#6E6553]">{label}</span>
              </div>
              <span className="font-serif text-2xl text-[#3C3527]" data-testid={testid}>
                {value}
              </span>
            </div>
          ))}
        </aside>
      </div>
    </section>
  );
}
